import {useEffect, useMemo} from 'react';

import {QuestionType} from 'common/constants/survey';
import {SurveySettings, SurveyValue, SurveyValues} from 'common/models/survey';
import {isEmpty, isNil} from 'lodash';
import {UseFormReturn} from 'react-hook-form';
import {useParams, useSearchParams} from 'react-router-dom';
import {MessageType} from 'ui/constants/MessageType';
import getDefaultValues from 'ui/utils/getDefaultValues';
import {getParsedField, getSearchParamsValues} from 'ui/utils/getSearchParamsValues';
import isSeries from 'ui/utils/isSeries';
import * as Metrika from 'ui/utils/metrika';
import {parsePostMessageData} from 'ui/utils/parsePostMessageData';

import usePersistForm from './usePersistForm';

export const {iframe, preview} = window.YC;

export const getQuestionTypeMap = (survey?: SurveySettings): Record<string, QuestionType> => {
    const result: Record<string, QuestionType> = {};
    survey?.pages?.forEach((page) =>
        page.items.forEach((item) => {
            if (isNil(item)) {
                return;
            }
            if (isSeries(item)) {
                item.items.forEach((question) => {
                    result[question.id] = question.type;
                });
            } else {
                result[item.id] = item.type;
            }
        }),
    );
    return result;
};

const usePreFillingSurvey = (
    methods: UseFormReturn<SurveyValues>,
    survey?: SurveySettings,
): [() => void] => {
    const {surveyId = ''} = useParams();
    const [searchParams] = useSearchParams();
    const questionTypeMap = useMemo(() => getQuestionTypeMap(survey), [survey]);

    const searchParamsValues = useMemo(
        () => getSearchParamsValues(searchParams, questionTypeMap),
        [searchParams, questionTypeMap],
    );

    const [localStorageValues, removeLocalStorageValues] = usePersistForm(
        surveyId,
        methods.control,
        !preview,
    );

    useEffect(() => {
        const setValue = ({data}: MessageEvent) => {
            const parsedData = parsePostMessageData(data);
            // https://docs.yandex-team.ru/cloud/forms/postmessage#sec_format
            if (parsedData?.message !== MessageType.SetQuestionValue) {
                return;
            }
            const field = getParsedField(parsedData.slug, parsedData.value, questionTypeMap);
            if (questionTypeMap[parsedData.slug]) {
                Metrika.defaultValues(
                    Metrika.DefaultValueType.Embed,
                    questionTypeMap[parsedData.slug],
                );
            }
            Object.entries(field).forEach(([key, value]) => {
                if (key && value) {
                    methods.setValue(key, value as SurveyValue);
                }
            });
        };

        if (iframe) {
            window.addEventListener('message', setValue);
        }
        return () => window.removeEventListener('message', setValue);
    }, [methods, questionTypeMap]);

    useEffect(() => {
        if (isEmpty(methods.getValues())) {
            const initialValues = getDefaultValues(survey, searchParamsValues, localStorageValues);
            methods.reset(initialValues);
        }
    }, [localStorageValues, methods, searchParamsValues, survey]);

    const clearValues = () => {
        methods.reset();
        removeLocalStorageValues();
    };

    return [clearValues];
};
export default usePreFillingSurvey;
